'use strict'

require('../scss/base/style.scss')

import SecurityManager from './lib/SecurityManager'
import LegacyStorage from './lib/storage/LegacyStorage'
import MemoryStorage from './lib/storage/MemoryStorage'
import DataAccess from './lib/DataAccess'
import ModelMapper from './lib/ModelMapper'
import NavigationManager from './lib/NavigationManager'

class Application {
  constructor () {
    this.version = '#buildVersion#'
    this.buildTimestamp = '#buildTimestamp#'
    this.message = (message) => {
      console.log('Got message', message)
      dispatchEvent(new Event(message))
    }
    this.session = {}

    this.securityManager = new SecurityManager()
    this.externalStorage = new LegacyStorage('/api')
    this.localStorage = new MemoryStorage()
    this.navigationManager = null
    this.dataAccess = new DataAccess(this.securityManager, this.localStorage, this.externalStorage, this.message)
    this.modelMapper = new ModelMapper()
    this.exceptionHandler = (e) => {
      if (e) {
        if (e.stack) {
          console.error(e, e.stack)
        } else {
          console.error(e)
        }
      } else {
        console.error('Uncaught exception')
      }
    }
    this.bootMessage = null
    this.reloadLink = null
    this.indicator = null
    this.viewport = null
    this.delayedNavigation = false
    this.License = null
  }

  getUtcTimestamp (date) {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  }

  getLocalTimestamp (date) {
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000)
  }

  init () {
    this.viewport = document.getElementById('viewport')
    let boot = document.getElementById('boot')

    this.bootMessage = document.createElement('div')
    this.bootMessage.className = 'messages'
    boot.appendChild(this.bootMessage)
    this.createReloadLink()
    boot.appendChild(this.reloadLink)
    this.createIndicator()
    document.body.appendChild(this.indicator)

    this.setMessage('Versie: ' + this.version + ' (' + this.buildTimestamp + ')')
    this.setMessage('Bezig met opstarten...')

    onerror = () => {
      this.setMessage('Helaas, door een onbekende fout met uw externe opslag kan PRIM niet gestart worden.')
      this.showReloadLink()
    }

    addEventListener('write', this.showWriteIndicator.bind(this))
    addEventListener('writePending', this.showWritePendingIndicator.bind(this))
    addEventListener('writeFailed', this.showWriteFailedIndicator.bind(this))
    addEventListener('read', this.showReadIndicator.bind(this))
    addEventListener('readPending', this.showReadPendingIndicator.bind(this))

    this.capabilityCheck()
  }

  writeStatus () {
    document.body.classList.remove('writeFailed')
    document.body.classList.add('writing')
  }

  writeFailedStatus () {
    document.body.classList.remove('writing')
    document.body.classList.add('writeFailed')
  }

  clearStatus () {
    document.body.classList.remove('writeFailed')
    document.body.classList.remove('writing')
  }

  setMessage (message) {
    this.bootMessage.innerHTML += message + '<br />'

    let isScrolledToBottom = this.bootMessage.scrollHeight - this.bootMessage.clientHeight <= this.bootMessage.scrollTop + 1

    if (isScrolledToBottom) {
      this.bootMessage.scrollTop = this.bootMessage.scrollHeight - this.bootMessage.clientHeight
    }
  }

  showReloadLink () {
    this.reloadLink.setAttribute('style', '')
  }

  initApplication () {
    this.initNavigation('/entercredentials/')
  }

  initExternalStorage () {
    this.setMessage('Controle gegevens uit externe opslag...')
    this.externalStorage.status().then(() => {
      this.externalStorage.open().then(() => {
        window.PRIM.navigate('/start/')
      }, () => {
        window.PRIM.navigate('/offline/')
      }).catch((e) => {
        window.PRIM.navigate('/offline/')
      })
    }, (status) => {
      switch (status) {
        case 'invalid':
          window.PRIM.navigate('/reentercredentials/')
          break
        default:
          window.PRIM.navigate('/offline/')
          break
      }
    }).catch((e) => {
      window.PRIM.navigate('/offline/')
    })
  }

  loadCredentials () {
    return new Promise((resolve, reject) => {
      this.localStorage.get({name: 'settings'}, 'credentials').then((credentials) => {
        if (credentials && credentials.username && credentials.password) {
          this.externalStorage.setCredentials(credentials.username, credentials.password)
          resolve()
          return
        }
        console.warn('Failed to retrieve settings from local storage.')
        reject()
      }, () => {
        console.warn('Failed to retrieve settings from local storage.')
        reject()
      })
    })
  }

  saveCredentials (username, password) {
    return new Promise((resolve, reject) => {
      this.localStorage.put({name: 'settings'}, {id: 'credentials', username: username, password: password}).then(resolve, () => {
        console.warn('Failed to persist settings from local storage.')
        reject()
      })
    })
  }

  capabilityCheck () {
    this.setMessage('Controle browser...')

    if (typeof Promise === 'undefined') {
      this.setMessage('Helaas, uw browser voldoet niet aan de eisen om deze applicatie te kunnen draaien. Promises zijn niet beschikbaar. Werk indien mogelijk uw browser naar een nieuwere versie bij.')
      this.showReloadLink()
      return
    }

    if (typeof window.crypto === 'undefined' || typeof window.crypto.subtle === 'undefined' && typeof window.crypto.webkitSubtle === 'undefined') {
      this.setMessage('Helaas, uw browser voldoet niet aan de eisen voor cryptografie. Veiligheid kan niet worden gegarandeerd. Werk indien mogelijk uw browser naar een nieuwere versie bij.')
      this.showReloadLink()
      return
    }

    this.initApplication()
  }

  createReloadLink (visible) {
    this.reloadLink = document.createElement('a')
    this.reloadLink.href = '/'
    if (!visible) {
      this.reloadLink.setAttribute('style', 'display: none')
    }
    this.reloadLink.innerHTML = 'Herstarten'
    this.reloadLink.onclick = (e) => {
      e.preventDefault()
      location.reload(true)
    }
  }

  createIndicator () {
    this.indicator = document.createElement('div')
    this.indicator.id = 'loader'
  }

  showWritePendingIndicator () {
    this.indicator.className = 'writePending'
  }

  showWriteFailedIndicator () {
    this.indicator.className = 'writeFailed'
  }

  showWriteIndicator () {
    this.indicator.className = 'write'
  }

  showReadPendingIndicator () {
    this.indicator.className = 'readPending'
  }

  showReadIndicator () {
    this.indicator.className = 'read'
  }

  initNavigation (start) {
    this.setMessage('Initialisatie navigatie...')
    this.navigationManager = new NavigationManager(this.viewport)
    if (!start) {
      window.PRIM.navigate('/welcome/')
    } else {
      window.PRIM.navigate(start)
    }
  }

  setLicense (license) {
    this.License = license
  }

  hasReadonlyModuleAccess (module) {
    return !this.License ||
      !this.License.valid ||
      (this.License.moduleAccess[module] && this.License.moduleAccess[module] !== 'readwrite' && this.License.moduleAccess[module] !== 'demo')
  }

  navigate (uri, replace) {
    if (replace) {
      history.replaceState(null, document.title, '#' + uri)
    } else {
      history.pushState(null, document.title, '#' + uri)
      this.navigationManager.handleNavigation()
    }
  } 

  cancelDelayedNavigation () {
    this.delayedNavigation = false
  }

  initDelayedNavigation () {
    this.delayedNavigation = true
  }

  isDelayedNavigationActive () {
    return this.delayedNavigation === true
  }

  getUUID () {
    let buffer = new Uint16Array(8)
    window.crypto.getRandomValues(buffer)

    let S4 = (num) => {
      let result = num.toString(16)
      while (result.length < 4) {
        result = '0' + result
      }
      return result
    }

    return S4(buffer[0]) +
      S4(buffer[1]) +
      '-' +
      S4(buffer[2]) +
      '-' +
      S4(buffer[3]) +
      '-' +
      S4(buffer[4]) +
      '-' +
      S4(buffer[5]) +
      S4(buffer[6]) +
      S4(buffer[7])
  }  
}

window.PRIM = null

if (typeof addEventListener === 'function') {
  addEventListener('load', () => {
    window.PRIM = new Application()
    onerror = window.PRIM.exceptionHandler
    window.PRIM.init()
  })
} else {
  window.onload = () => {
    let boot = document.getElementById('boot')
    let bootMessage = document.createElement('div')
    bootMessage.innerHTML = 'Helaas, uw browser voldoet niet aan de eisen om deze applicatie te kunnen draaien. Werk indien mogelijk uw browser naar een nieuwere versie bij.'
    let reloadLink = document.createElement('a')
    reloadLink.href = '.'
    reloadLink.setAttribute('style', 'display: none')
    reloadLink.innerText = 'Herstarten'
    reloadLink.onclick = (e) => {
      e.preventDefault()
      location.reload(true)
    }
    boot.appendChild(bootMessage)
    boot.appendChild(reloadLink)
  }
}



// WEBPACK FOOTER //
// ./js/app.js